import { useNavigate } from 'react-router-dom';
import { Moon, LogOut, User, ArrowLeft } from 'lucide-react';
import { useTelegram, useStore } from '@/core/providers';
import { authService } from '@/core/api';
import { useI18n, LANGUAGES } from '@/core/i18n';

function SettingsPage() {
  const navigate = useNavigate();
  const { hapticFeedback, showConfirm, showAlert, user: tgUser } = useTelegram();
  const { settings, updateSettings, user, isAuthenticated, setUser } = useStore();
  const { t, setLanguage } = useI18n();

  const handleLanguageChange = (code: string) => {
    hapticFeedback('selection');
    updateSettings({ language: code });
    setLanguage(code as any);
  };

  const handleLogout = async () => {
    const confirmed = await showConfirm(t('settings.confirm.signOut'));
    if (confirmed) {
      hapticFeedback('impact');
      await authService.logout();
      setUser(null);
      showAlert(t('settings.toast.signedOut'));
      navigate('/feed');
    }
  };

  return (
    <div className="h-full bg-telegram-bg">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>

      <div className="p-4 pt-20 space-y-6">
        {/* User Profile */}
        {isAuthenticated && user && (
          <section>
            <h2 className="text-sm font-medium text-telegram-hint mb-2 uppercase">
              {t('settings.section.account')}
            </h2>
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-telegram-button text-telegram-button-text rounded-full flex items-center justify-center">
                  {tgUser?.photo_url ? (
                    <img
                      src={tgUser.photo_url}
                      alt=""
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User size={24} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-telegram-text">
                    {tgUser?.first_name || user.email}
                  </p>
                  <p className="text-sm text-telegram-hint">{user.email}</p>
                </div>
                <span className="text-xs bg-telegram-button/10 text-telegram-button px-2 py-1 rounded capitalize">
                  {user.plan}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Language */}
        <section>
          <h2 className="text-sm font-medium text-telegram-hint mb-2 uppercase">
            {t('settings.section.language')}
          </h2>
          <div className="card space-y-1">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-telegram-bg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{lang.flag}</span>
                  <span className="text-telegram-text">{lang.label}</span>
                </div>
                {settings.language === lang.code && (
                  <div className="w-5 h-5 bg-telegram-button rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-telegram-button-text rounded-full" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Appearance */}
        <section>
          <h2 className="text-sm font-medium text-telegram-hint mb-2 uppercase">
            {t('settings.section.appearance')}
          </h2>
          <div className="card space-y-3">
            <div className="w-full flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Moon size={20} className="text-telegram-hint" />
                <span className="text-telegram-text">{t('settings.theme')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] p-1">
              {(['system', 'light', 'dark'] as const).map((option) => {
                const isActive = settings.theme === option;
                return (
                  <button
                    key={option}
                    onClick={() => {
                      hapticFeedback('selection');
                      updateSettings({ theme: option });
                    }}
                    className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-[var(--ff-surface)] text-telegram-text shadow-sm'
                        : 'text-telegram-hint hover:text-telegram-text'
                    }`}
                    aria-pressed={isActive}
                    type="button"
                  >
                    {t(`settings.themeOption.${option}`)}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Account Actions */}
        {isAuthenticated ? (
          <section>
            <button
              onClick={handleLogout}
              className="w-full card flex items-center gap-3 text-red-500"
            >
              <LogOut size={20} />
              <span>{t('settings.signOut')}</span>
            </button>
          </section>
        ) : (
          <section>
            <button
              onClick={() => navigate('/login')}
              className="w-full btn-primary"
            >
              {t('settings.signIn')}
            </button>
          </section>
        )}

        {/* App Info */}
        <div className="text-center text-xs text-telegram-hint pt-4">
          <p>{t('settings.appVersion')}</p>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
